import {
  AUTO_CURRENCY_MAX_FRACTION_DIGITS,
  AUTO_CURRENCY_MIN_FRACTION_DIGITS,
  AUTO_FLOAT_MAX_FRACTION_DIGITS,
  CELL_ALIGN_CLASS_PREFIX,
  DEFAULT_CELL_ALIGN,
  FLOAT_DEFAULT_FRACTION_DIGITS,
  GROUP_ROW_DETAIL,
  NUMERIC_TEXT_VALUE_CLASS,
  PERCENTAGE_DEFAULT_FRACTION_DIGITS,
  PERCENTAGE_FROM_FRACTION_SUB_TYPE,
  TARGET_PERCENTAGE_MAX_FRACTION_DIGITS,
} from "../types/constants";
import { percentageFromFractionValue } from "./numberUtils";
import { takesGroupingValue } from "./rangeGroupingUtils";

const isBlank = (v) => v === undefined || v === null || v === "";

/** Returns fraction-digit options showing exactly the given number of places. */
export function fixedFractionDigitOptions(digits) {
  return { minimumFractionDigits: digits, maximumFractionDigits: digits };
}

/** Returns the fraction-digit options for a TARGET_PERCENTAGE column. */
export function targetPercentageFractionDigitOptions(col, decimals) {
  if (decimals !== undefined && decimals !== null) {
    return fixedFractionDigitOptions(decimals);
  }
  if (col._type === "FLOAT") {
    return { maximumFractionDigits: TARGET_PERCENTAGE_MAX_FRACTION_DIGITS };
  }
  return fixedFractionDigitOptions(PERCENTAGE_DEFAULT_FRACTION_DIGITS);
}

/** Formats a number as a percentage without thousands separators. */
export function percentageText(num, fractionDigitOptions) {
  return `${num.toLocaleString(undefined, {
    ...fractionDigitOptions,
    useGrouping: false,
  })}%`;
}

/** Returns the auto fraction-digit options for a currency value. */
export function autoCurrencyFractionDigitOptions(num) {
  const rounded = Number(num.toFixed(AUTO_CURRENCY_MAX_FRACTION_DIGITS));
  if (Number.isInteger(rounded)) return { maximumFractionDigits: 0 };
  return {
    minimumFractionDigits: AUTO_CURRENCY_MIN_FRACTION_DIGITS,
    maximumFractionDigits: AUTO_CURRENCY_MAX_FRACTION_DIGITS,
  };
}

/** Returns the grid valueFormatter for a column's type, or null when it has none. */
export function selectValueFormatter(
  col,
  { fractionDigitOptions, getEnumDisplayValue }
) {
  const isCurrencyColumn = col._subType === "CURRENCY";
  const isFractionPercentage =
    col._subType === PERCENTAGE_FROM_FRACTION_SUB_TYPE;
  const isPercentageColumn =
    col._subType === "PERCENTAGE" || isFractionPercentage;
  const isTargetPercentage = col._subType === "TARGET_PERCENTAGE";
  const isYearColumn = col._subType === "YEAR";
  const isEnumColumn = col._type === "ENUM";
  const isFloatColumn =
    col._type === "FLOAT" &&
    !isCurrencyColumn &&
    !isPercentageColumn &&
    !isTargetPercentage &&
    !isYearColumn;
  const isPlainFloatColumn = isFloatColumn && !col._subType;

  if (isEnumColumn) {
    return (params) => {
      const rawValue = params?.value ?? params;
      return getEnumDisplayValue(col.column, rawValue) ?? rawValue;
    };
  }
  if (isYearColumn) {
    return (params) => {
      const v = params?.value ?? params;
      if (isBlank(v)) return "";
      const num = Math.round(Number(v));
      if (Number.isNaN(num)) return String(v);
      return String(num);
    };
  }
  if (isFloatColumn) {
    const floatOptions =
      fractionDigitOptions ??
      (isPlainFloatColumn
        ? { maximumFractionDigits: AUTO_FLOAT_MAX_FRACTION_DIGITS }
        : fixedFractionDigitOptions(FLOAT_DEFAULT_FRACTION_DIGITS));
    return (params) => {
      const v = params?.value ?? params;
      if (isBlank(v)) return "";
      const num = Number(v);
      if (Number.isNaN(num)) return String(v);
      return num.toLocaleString(undefined, floatOptions);
    };
  }
  if (isPercentageColumn) {
    const percentageOptions =
      fractionDigitOptions ??
      fixedFractionDigitOptions(PERCENTAGE_DEFAULT_FRACTION_DIGITS);
    return (params) => {
      const rawValue = params?.value ?? params;
      const v = isFractionPercentage
        ? percentageFromFractionValue(rawValue)
        : rawValue;
      if (isBlank(v)) return "";
      const num = Number(v);
      if (Number.isNaN(num)) return String(v);
      return percentageText(num, percentageOptions);
    };
  }
  if (isCurrencyColumn) {
    return (params) => {
      const v = params?.value ?? params;
      if (isBlank(v)) return "";
      const num = Number(v);
      if (Number.isNaN(num)) return String(v);
      return `$${num.toLocaleString(
        undefined,
        fractionDigitOptions ?? autoCurrencyFractionDigitOptions(num)
      )}`;
    };
  }
  return null;
}

/** Returns a valueFormatter matching the grid's default for a date or number column, or null. */
export function defaultTypeValueFormatter(gridType) {
  if (gridType === "date") {
    return (value) => {
      if (isBlank(value)) return "";
      if (value instanceof Date) return value.toLocaleDateString();
      return String(value);
    };
  }
  if (gridType === "number") {
    return (value) => {
      if (isBlank(value)) return "";
      if (typeof value === "number") return value.toLocaleString();
      return String(value);
    };
  }
  return null;
}

/** Returns a valueFormatter showing the raw label on Group rows and the base format elsewhere. */
export function rangeGroupedValueFormatter(baseFormatter, controlColumn) {
  return (value, row) => {
    if (takesGroupingValue(row, controlColumn)) {
      return value === null || value === undefined ? "" : String(value);
    }
    if (baseFormatter) return baseFormatter(value, row);
    return plainValueFormatter(value);
  };
}

/** Returns whether the row is a group row: one that takes the grouping value, or one still holding its empty detail list (sub-totals without details). */
export function isGroupRow(row, controlColumn) {
  return (
    takesGroupingValue(row, controlColumn) ||
    Array.isArray(row?.[controlColumn])
  );
}

/** Returns a valueFormatter showing blankLabel for an empty value on group rows and the base format elsewhere. */
export function blankGroupValueFormatter(
  baseFormatter,
  controlColumn,
  blankLabel
) {
  return (value, row, ...rest) => {
    if (isBlank(value) && isGroupRow(row, controlColumn)) return blankLabel;
    if (baseFormatter) return baseFormatter(value, row, ...rest);
    return plainValueFormatter(value);
  };
}

/** Returns a renderCell showing blankLabel for an empty value on group rows and the base renderer elsewhere. Renderers such as the checkbox and chips draw from the raw value, so they never see the formatter's label. */
export function blankGroupRenderer(baseRenderer, controlColumn, blankLabel) {
  return (params) =>
    isBlank(params.value) && isGroupRow(params.row, controlColumn)
      ? blankLabel
      : baseRenderer(params);
}

/** Formats a value as blank, a locale number or text. */
export function plainValueFormatter(value) {
  if (isBlank(value)) return "";
  if (typeof value === "number") return Number(value).toLocaleString();
  return String(value);
}

/** Returns whether the row is a Detail row. */
export function isDetailRow(row, controlColumn) {
  return Boolean(controlColumn) && row?.[controlColumn] === GROUP_ROW_DETAIL;
}

/** Returns a (value, row, ...rest) function using detailFn on Detail rows and otherFn elsewhere. */
export function detailRowAware(
  detailFn,
  otherFn,
  controlColumn,
  fallback = plainValueFormatter
) {
  if (detailFn === otherFn || !controlColumn) return otherFn;
  const detail = detailFn ?? fallback;
  const other = otherFn ?? fallback;
  return (value, row, ...rest) =>
    (isDetailRow(row, controlColumn) ? detail : other)(value, row, ...rest);
}

const resolveCellClassName = (cellClassName, params) =>
  (typeof cellClassName === "function"
    ? cellClassName(params)
    : cellClassName) ?? "";

/** Returns a cellClassName giving Detail rows the input type's classes and alignment, other rows the output type's classes. */
export function detailRowAwareCellClassName(
  inputTypeProps,
  outputTypeProps,
  controlColumn
) {
  if (inputTypeProps === outputTypeProps || !controlColumn) {
    return outputTypeProps.cellClassName;
  }
  const outputAlign = outputTypeProps.align ?? DEFAULT_CELL_ALIGN;
  return (params) => {
    if (!isDetailRow(params.row, controlColumn)) {
      return resolveCellClassName(outputTypeProps.cellClassName, params);
    }
    const classes = resolveCellClassName(inputTypeProps.cellClassName, params);
    const inputAlign = classes.split(" ").includes(NUMERIC_TEXT_VALUE_CLASS)
      ? DEFAULT_CELL_ALIGN
      : inputTypeProps.align ?? DEFAULT_CELL_ALIGN;
    if (inputAlign === outputAlign) return classes;
    return [classes, CELL_ALIGN_CLASS_PREFIX + inputAlign]
      .filter(Boolean)
      .join(" ");
  };
}

/** Returns the cell text the grid shows by default. */
export function formattedValueRenderer(params) {
  return String(params.formattedValue ?? params.value ?? "");
}

/** Returns a renderer using detailRenderer on Detail rows and otherRenderer elsewhere. */
export function detailRowAwareRenderer(
  detailRenderer,
  otherRenderer,
  controlColumn
) {
  if (
    detailRenderer === null ||
    detailRenderer === undefined ||
    detailRenderer === otherRenderer ||
    !controlColumn
  ) {
    return otherRenderer;
  }
  const other = otherRenderer ?? formattedValueRenderer;
  return (params) =>
    (isDetailRow(params.row, controlColumn) ? detailRenderer : other)(params);
}
